// src/context/SignalRContext.js
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import * as signalR from "@microsoft/signalr";
import { createNotificationHub } from '../pages/service/notificationHub';
import { useAuth } from './AuthContext';

const SignalRContext = createContext(null);

export const useSignalR = () => {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error('useSignalR must be used within a SignalRProvider');
  }
  return context;
};

export const SignalRProvider = ({ children }) => {
  const { user, token } = useAuth(); // Now token comes from AuthContext
  const [connection, setConnection] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const connectionRef = useRef(null);
  const [notifications, setNotifications] = useState([]);

  // Log auth state on every change
  useEffect(() => {
    console.log('🔐 SignalR Context - Auth State:', { 
      hasUser: !!user, 
      hasToken: !!token,
      userId: user?.id,
      userName: user?.name,
      tokenLength: token?.length
    });
  }, [user, token]);

  // Initialize connection
  useEffect(() => {
    if (!token || !user) {
      console.log('⏳ SignalR waiting for authentication... Token:', !!token, 'User:', !!user);
      if (connectionRef.current) {
        console.log('🛑 Stopping existing SignalR connection due to missing auth');
        connectionRef.current.stop();
        connectionRef.current = null;
        setConnection(null);
        setIsConnected(false);
      }
      return;
    }

    console.log('🚀 Starting SignalR connection with token (first 20 chars):', token.substring(0, 20) + '...');

    const startConnection = async () => {
      try {
        // Stop existing connection if any
        if (connectionRef.current) {
          console.log('🔄 Stopping existing SignalR connection...');
          await connectionRef.current.stop();
          connectionRef.current = null;
        }

        // Create new connection
        console.log('🔌 Creating new SignalR hub connection...');
        const hubConnection = createNotificationHub(token);
        connectionRef.current = hubConnection;

        // Set up event handlers before starting
        hubConnection.onclose((error) => {
          console.log('🔌 SignalR connection closed:', error);
          setIsConnected(false);
          setConnectionError(error?.message || 'Connection closed');
        });

        hubConnection.onreconnecting((error) => {
          console.log('🔄 SignalR reconnecting:', error);
          setIsConnected(false);
        });

        hubConnection.onreconnected((connectionId) => {
          console.log('✅ SignalR reconnected with ID:', connectionId);
          setIsConnected(true);
          setConnectionError(null);
        });

        // Start connection
        console.log('🚀 Attempting to start SignalR connection...');
        await hubConnection.start();
        console.log('✅ SignalR connected successfully!');
        console.log('📡 Connection ID:', hubConnection.connectionId);
        console.log('📊 Connection State:', signalR.HubConnectionState[hubConnection.state]);
        
        setIsConnected(true);
        setConnectionError(null);
        setConnection(hubConnection);

        // Test ping to verify connection
        try {
          console.log('🏓 Sending ping...');
          await hubConnection.invoke('Ping');
          console.log('🏓 Ping successful - connection is working!');
        } catch (pingError) {
          console.warn('⚠️ Ping failed but connection might still work:', pingError);
        }

      } catch (error) {
        console.error('❌ Failed to connect to SignalR:', error);
        console.error('❌ Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        setConnectionError(error.message);
        setIsConnected(false);
        
        // Attempt to reconnect after delay
        console.log('🔄 Will attempt to reconnect in 5 seconds...');
        setTimeout(startConnection, 5000);
      }
    };

    startConnection();

    // Cleanup on unmount or user/token change
    return () => {
      if (connectionRef.current) {
        console.log('🧹 Cleaning up SignalR connection');
        connectionRef.current.stop();
        connectionRef.current = null;
      }
    };
  }, [token, user]); // This will now re-run when token changes

  // Monitor connection state
  useEffect(() => {
    if (connection) {
      console.log('📊 Connection state changed:', signalR.HubConnectionState[connection.state]);
    }
  }, [connection?.state]);

  // Reconnection logic with more logging
  useEffect(() => {
    if (!connection) return;

    const handleNewNotification = (notification) => {
      console.log('🔔 New notification received:', notification);
      setNotifications(prev => [notification, ...prev]);
      
      // You can add UI toast/alert here
      if (window.notify) {
        window.notify({
          type: 'info',
          message: notification.message || 'New task notification'
        });
      }
    };

    connection.on('ReceiveNewNotification', handleNewNotification);

    return () => {
      connection.off('ReceiveNewNotification', handleNewNotification);
    };
  }, [connection]);

  const value = {
    connection,
    isConnected,
    connectionError,
    notifications  // Add this to context
  };

  return (
    <SignalRContext.Provider value={value}>
      {children}
    </SignalRContext.Provider>
  );
};